import {BaseStore, globalStore, StoreClass, StoreObject} from "../../stem-core/state/Store";
import {StemDate} from "../../stem-core/time/Date";
import {apiClient} from "../connection/BlinkApiClient";
import {capitalize} from "../../blinkpay/Utils";
import {field} from "../../stem-core/state/StoreField";
import {PaymentProcessor} from "./PaymentProcessorStore";
import {iFrameMerchantService} from "../../blinkpay/services/IframeMerchantService";
import {StoreEvent} from "../../stem-core/state/State";
import {Dispatchable} from "../../stem-core/base/Dispatcher";

interface PaymentMethodDetails {
    last4: string;
    brand: string;
    expMonth: string;
    expYear: string;
}


@globalStore
export class PaymentMethod extends BaseStore("PaymentMethod") {
    static Scope = {
        PUBLIC: "public",
        PRIVATE: "private",
    };

    @field(Number) userId: number;
    @field("PaymentProcessor") paymentProcessor: PaymentProcessor;
    @field(Date) createdAt: StemDate;
    @field(Date) deletedAt: StemDate;
    @field(Boolean) isPrimary: boolean;
    @field(Object) details: PaymentMethodDetails;
    @field(String) scope: string; // TODO Use enum
    @field(Number) scopeResourceId: number;

    getLastDigits(): string {
        const {last4} = this.details;
        return last4;
    }

    getBrand(): string {
        let brand = this.details.brand || "Card";
        if (brand.toLowerCase() === "american express") {
            // American Express is too long in string messages and breaks some UI elements
            brand = "AmEx";
        }
        brand = capitalize(brand);
        return brand;
    }

    getExpiryDate(): StemDate | null {
        const {expMonth, expYear} = this.details;
        if (!expMonth || !expYear) {
            return null;
        }
        return new StemDate(expMonth + "/01/" + expYear).getEndOfMonth();
    }

    // The list of payment processors should be the ones that are available for this merchant
    isAvailable(paymentProcessors: any[], merchant: any = iFrameMerchantService.getMerchant()): boolean {
        if (merchant) {
            const ownPaymentProcessor = this.paymentProcessor;
            if (!ownPaymentProcessor.canServiceMerchant(merchant)) {
                return false;
            }
            for (const otherPaymentProcessor of paymentProcessors) {
                if (ownPaymentProcessor.provider === otherPaymentProcessor.provider
                    && ownPaymentProcessor.isBlinkGlobal()
                    && !otherPaymentProcessor.isBlinkGlobal()) {
                    return false;
                }
            }
        }
        return !this.deletedAt && this.scope === PaymentMethod.Scope.PUBLIC;
    }

    static getPaymentMethodsForRecurringPayment(recurringPayment: any): PaymentMethod[] {
        // TODO @payments: This is wrong. What if recurringPayment is a DONATION and the payment
        //  method was used for a SUBSCRIPTION with the same ID?
        return this.all().filter(paymentMethod => {
            return !paymentMethod.deletedAt && paymentMethod.scope === PaymentMethod.Scope.PRIVATE
            && (paymentMethod.scopeResourceId === recurringPayment.id || paymentMethod.scopeResourceId == null)
        });
    }

    static getPrimary(): PaymentMethod | undefined {
        return this.getAvailablePaymentMethods().find(paymentMethod => paymentMethod.isPrimary);
    }

    static getAvailablePaymentMethods(merchant: any = iFrameMerchantService.getMerchant()): PaymentMethod[] {
        const paymentProcessors = PaymentProcessor.filter(paymentProcessor => !merchant || paymentProcessor.canServiceMerchant(merchant));
        const paymentMethods = this.filter(paymentMethod => paymentMethod.isAvailable(paymentProcessors, merchant));

        // First the primary ones, then the rest
        return paymentMethods.sort((a, b) => {
            if (a.isPrimary === b.isPrimary) {
                return 0;
            }
            return a.isPrimary ? -1 : 1;
        });
    }

    static getDefault(): PaymentMethod | undefined {
        const availablePaymentMethods = this.getAvailablePaymentMethods();
        return availablePaymentMethods.find(paymentMethod => paymentMethod.isPrimary) || availablePaymentMethods[0];
    }

    static applyDeleteEvent<T extends StoreObject>(this: StoreClass<T> & Dispatchable, event: StoreEvent): T | undefined {
        const deletedObj = this.getObjectForEvent<T>(event) as any as PaymentMethod;
        if (!deletedObj) {
            return;
        }
        if (!deletedObj.deletedAt) {
            deletedObj.deletedAt = StemDate.now();
        }
        deletedObj.dispatch("delete", event, deletedObj);
        this.dispatch("delete", deletedObj, event);
        return deletedObj as any;
    }
}

interface PaymentMethodToken {
    id: string;
    [key: string]: any;
}

export async function apiAddPaymentMethodToken(token: PaymentMethodToken, scope: string, paymentProcessor: PaymentProcessor): Promise<PaymentMethod> {
    const response = await apiClient.post("/payment_methods/add_payment_method", {
        tokenId: token.id,
        scope,
        rawToken: JSON.stringify(token),
        paymentProcessorId: paymentProcessor.id,
    });
    return PaymentMethod.loadObject(response);
}

export async function apiSetPrimaryPaymentMethod(paymentMethodId: string | number): Promise<PaymentMethod> {
    const response = await apiClient.post("/payment_methods/set_primary_payment_method", {paymentMethodId});
    return PaymentMethod.loadObject(response);
}

export async function apiDeletePaymentMethod(paymentMethodId: string | number): Promise<any> {
    return apiClient.post("/payment_methods/delete_payment_method", {paymentMethodId});
}
